/**
 * Slice 42 — Check step after Walk photos: OK / Problem per owner quick-check.
 * Problem opens fullscreen camera for that specific task (fresh photo required).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Camera, Check, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";
import { useCreateMaintenanceReport } from "@/hooks/useMaintenanceReports";
import {
  readSimpleFlowState,
  type InspectFinding,
  type SimpleFlowChromeOpts,
  type SimpleFlowState,
} from "@/lib/simpleTurnover";
import {
  appendPhotoCaptureRecord,
  captureFrameFromVideo,
  uploadInAppCleaningPhoto,
  type CleaningPhotoCaptureRecord,
} from "@/lib/cleaningPhotoCapture";
import { simpleFlowSegmentIndex } from "@/lib/turnoverWizardPlan";
import { SimpleStepHeader } from "@/components/cleaner/SimpleStepHeader";
import { CLEANER_SIMPLE_CTA_FOOTER, CLEANER_SIMPLE_STEP_SHELL } from "@/lib/turnoverChrome";
import { MediaThumb } from "@/components/media/MediaThumb";

export type WalkQuickCheckItem = { id: string; label: string };

interface Props {
  propertyId: string;
  taskId: string;
  cleanerId?: string | null;
  checklistName?: string | null;
  entryWalkthroughState: unknown;
  quickChecks: WalkQuickCheckItem[];
  includeMaintenance?: boolean;
  includeQuickCheck?: boolean;
  includeGuestSetup?: boolean;
  onComplete: () => void;
  onBack?: () => void;
  onSelectSegment?: (index: number) => void;
}

export function SimpleQuickCheckStep({
  propertyId,
  taskId,
  cleanerId,
  checklistName,
  entryWalkthroughState,
  quickChecks,
  includeMaintenance = false,
  includeQuickCheck = true,
  includeGuestSetup = false,
  onComplete,
  onBack,
  onSelectSegment,
}: Props) {
  const { t, tDynamic } = useTranslation();
  const chrome: SimpleFlowChromeOpts = { includeMaintenance, includeQuickCheck, includeGuestSetup };
  const createReport = useCreateMaintenanceReport();
  const initial = readSimpleFlowState(entryWalkthroughState);
  const rows = quickChecks;

  const [answers, setAnswers] = useState<Record<string, "ok" | "problem">>(() => {
    const raw = initial.walk_quick_checks;
    return raw && typeof raw === "object" ? { ...raw } : {};
  });
  const [photos, setPhotos] = useState<Record<string, string>>(() => {
    const raw = initial.walk_quick_check_photos;
    return raw && typeof raw === "object" ? { ...raw } : {};
  });
  const [findings, setFindings] = useState<InspectFinding[]>(
    () => (initial.inspect_findings ?? []).filter((f) => f.status === "missing" || f.status === "broken"),
  );
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(false);

  const [captureForId, setCaptureForId] = useState<string | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const shutterLock = useRef(false);

  const left = rows.filter((r) => !answers[r.id]).length;
  const allDone = rows.length === 0 || left === 0;
  const captureLabel = rows.find((r) => r.id === captureForId)?.label ?? t("photoTheProblem");

  const persist = useCallback(
    async (patch: Partial<SimpleFlowState>) => {
      const prev = readSimpleFlowState(entryWalkthroughState);
      const merged: SimpleFlowState = { ...prev, simple_flow: true, ...patch };
      const { error } = await supabase
        .from("cleaning_tasks")
        .update({ entry_walkthrough_state: merged })
        .eq("id", taskId);
      if (error) throw error;
    },
    [entryWalkthroughState, taskId],
  );

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraReady(false);
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError(t("couldNotOpenCamera"));
      return;
    }
    try {
      stopCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      });
      streamRef.current = stream;
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        video.setAttribute("playsinline", "true");
        video.muted = true;
        await video.play().catch(() => undefined);
      }
      setCameraReady(true);
    } catch (err) {
      console.error("[SimpleQuickCheckStep] camera", err);
      const name = err instanceof Error ? err.name : "";
      setCameraError(
        name === "NotAllowedError"
          ? t("allowCameraAccess")
          : t("couldNotOpenCamera"),
      );
      setCameraReady(false);
    }
  }, [stopCamera]);

  useEffect(() => {
    if (captureForId) {
      void startCamera();
    } else {
      stopCamera();
    }
    return () => stopCamera();
  }, [captureForId, startCamera, stopCamera]);

  const markOk = async (id: string) => {
    const nextAnswers = { ...answers, [id]: "ok" as const };
    const nextPhotos = { ...photos };
    delete nextPhotos[id];
    setAnswers(nextAnswers);
    setPhotos(nextPhotos);
    try {
      await persist({
        walk_quick_checks: nextAnswers,
        walk_quick_check_photos: nextPhotos,
      });
    } catch {
      toast({ title: t("couldNotSave"), variant: "destructive" });
    }
  };

  const undoAnswer = async (id: string) => {
    const nextAnswers = { ...answers };
    delete nextAnswers[id];
    const removedPhoto = photos[id];
    const nextPhotos = { ...photos };
    delete nextPhotos[id];
    const label = rows.find((r) => r.id === id)?.label;
    const nextFindings = findings.filter(
      (f) => !(removedPhoto && f.evidence_photo_url === removedPhoto) && !(label && f.note === label && f.status === "broken"),
    );
    setAnswers(nextAnswers);
    setPhotos(nextPhotos);
    setFindings(nextFindings);
    try {
      await persist({
        walk_quick_checks: nextAnswers,
        walk_quick_check_photos: nextPhotos,
        inspect_findings: nextFindings,
      });
    } catch {
      toast({ title: t("couldNotUndo"), variant: "destructive" });
    }
  };

  const openProblemCamera = (id: string) => {
    if (busy) return;
    setCaptureForId(id);
  };

  const captureProblemPhoto = async () => {
    if (!captureForId || shutterLock.current || busy) return;
    const video = videoRef.current;
    if (!video || !cameraReady) {
      toast({
        title: t("cameraRequired"),
        description: t("cameraRequiredCheckDesc"),
        variant: "destructive",
      });
      void startCamera();
      return;
    }
    shutterLock.current = true;
    setBusy(true);
    try {
      const frame = await captureFrameFromVideo(video);
      const { url, record } = await uploadInAppCleaningPhoto({
        propertyId,
        taskId,
        moment: "walk",
        blob: frame.blob,
        width: frame.width,
        height: frame.height,
        storagePath: `${propertyId}/${taskId}/simple-before/check/${crypto.randomUUID()}.jpg`,
      });

      const label = rows.find((r) => r.id === captureForId)?.label ?? "Quick check";
      const finding: InspectFinding = {
        status: "broken",
        note: label,
        evidence_photo_url: url,
        reference_photo_url: null,
        reference_photo_id: null,
      };
      const nextFindings = [
        ...findings.filter((f) => !(f.note === label && f.evidence_photo_url && photos[captureForId] === f.evidence_photo_url)),
        finding,
      ];
      const nextAnswers = { ...answers, [captureForId]: "problem" as const };
      const nextPhotos = { ...photos, [captureForId]: url };
      const prev = readSimpleFlowState(entryWalkthroughState);

      setFindings(nextFindings);
      setAnswers(nextAnswers);
      setPhotos(nextPhotos);
      setFlash(true);
      window.setTimeout(() => setFlash(false), 280);

      await persist({
        inspect_findings: nextFindings,
        walk_quick_checks: nextAnswers,
        walk_quick_check_photos: nextPhotos,
        photo_captures: appendPhotoCaptureRecord(
          prev.photo_captures,
          record as CleaningPhotoCaptureRecord,
        ),
      });

      if (cleanerId) {
        try {
          await createReport.mutateAsync({
            property_id: propertyId,
            cleaning_task_id: taskId,
            cleaner_id: cleanerId,
            checklist_name: checklistName || undefined,
            room_name: "Check",
            response: "needs_repair",
            notes: `PROBLEM: ${label}`,
            photos: [url],
            task_description: label,
          });
        } catch {
          /* finding still on task */
        }
      }

      stopCamera();
      setCaptureForId(null);
    } catch (err) {
      toast({
        title: t("captureFailed"),
        description: err instanceof Error ? err.message : t("tryAgain"),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
      shutterLock.current = false;
    }
  };

  const finish = async () => {
    if (!allDone) {
      toast({ title: t("nLeft").replace("{{count}}", String(left)), description: t("okOrProblemEach") });
      return;
    }
    setBusy(true);
    try {
      await persist({
        walk_quick_checks: answers,
        walk_quick_check_photos: photos,
        inspect_findings: findings,
      });
      stopCamera();
      onComplete();
    } catch (err) {
      toast({
        title: t("couldNotContinue"),
        description: err instanceof Error ? err.message : t("tryAgain"),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  if (captureForId) {
    return (
      <div className={cn(CLEANER_SIMPLE_STEP_SHELL, "relative bg-[#1c2732] text-white")}>
        <div className="flex h-14 shrink-0 items-center justify-between px-3">
          <button
            type="button"
            onClick={() => {
              if (busy) return;
              stopCamera();
              setCaptureForId(null);
            }}
            className="flex h-12 w-12 items-center justify-center"
            aria-label={t("cancel")}
          >
            <X className="h-6 w-6 text-white" strokeWidth={2.4} />
          </button>
          <div className="text-[17px] font-bold">{t("photoTheProblem")}</div>
          <div className="w-12" />
        </div>

        <div className="relative min-h-0 flex-1 overflow-hidden">
          <video
            ref={videoRef}
            playsInline
            muted
            autoPlay
            className={cn(
              "absolute inset-0 h-full w-full object-cover",
              !cameraReady && "opacity-0",
            )}
          />
          {!cameraReady ? (
            <div className="absolute inset-0 z-[1] flex flex-col items-center justify-center gap-3 px-6 text-center">
              <Camera className="h-10 w-10 text-white/50" strokeWidth={1.8} />
              <p className="text-[15px] font-medium text-white/70">
                {cameraError || t("startingCamera")}
              </p>
              <button
                type="button"
                onClick={() => void startCamera()}
                className="mt-1 rounded-full bg-[#24a89a] px-5 py-3 text-[15px] font-bold text-white"
              >
                {t("openCamera")}
              </button>
            </div>
          ) : null}

          {flash ? (
            <div className="pointer-events-none absolute inset-0 z-[3] animate-[checkFlash_0.28s_ease-out] bg-white" />
          ) : null}

          <div className="absolute left-4 right-4 top-2 z-[2] flex items-center gap-2.5 rounded-[14px] bg-[rgba(15,23,32,0.75)] px-4 py-3">
            <AlertTriangle className="h-4 w-4 shrink-0 text-[#f3a9a1]" strokeWidth={2.4} />
            <div className="min-w-0 text-[15.5px] font-bold leading-snug text-white">
              {captureLabel}
            </div>
          </div>

          <div className="absolute inset-x-0 bottom-0 z-[1] flex justify-center bg-gradient-to-t from-[rgba(15,23,32,0.86)] to-transparent px-[18px] pb-8 pt-10">
            <button
              type="button"
              disabled={busy}
              onClick={() => void captureProblemPhoto()}
              aria-label={t("capture")}
              className="flex h-[84px] w-[84px] items-center justify-center rounded-full border-[5px] border-white/30 bg-white active:scale-95 disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-7 w-7 animate-spin text-[#24a89a]" /> : null}
            </button>
          </div>
        </div>
        <style>{`@keyframes checkFlash { from { opacity: 0.85; } to { opacity: 0; } }`}</style>
      </div>
    );
  }

  return (
    <div className={cn(CLEANER_SIMPLE_STEP_SHELL, "relative text-[#0f1720]")}>
      <SimpleStepHeader
        title={t("checkTitle")}
        activeSegment={simpleFlowSegmentIndex("walk_checks", chrome)}
        includeMaintenance={includeMaintenance}
        includeQuickCheck={includeQuickCheck}
        includeGuestSetup={includeGuestSetup}
        onBack={onBack}
        onSelectSegment={onSelectSegment}
      />

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3.5 pb-2 [-webkit-overflow-scrolling:touch]">
        <div className="mb-3 flex items-baseline justify-between px-1">
          <div className="text-[15px] font-bold">{t("testThese")}</div>
          <div
            className={cn(
              "text-[13px] font-bold",
              allDone ? "text-[#15776d]" : "text-[#8494a2]",
            )}
          >
            {allDone ? t("allAnswered") : t("nLeft").replace("{{count}}", String(left))}
          </div>
        </div>

        <div className="flex flex-col gap-3">
          {rows.map((row) => {
            const ans = answers[row.id];
            const photo = photos[row.id];
            if (ans === "ok") {
              return (
                <div
                  key={row.id}
                  className="flex items-center gap-3 rounded-2xl border-[1.5px] border-[#24a89a] bg-white px-3.5 py-3.5"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#24a89a]">
                    <Check className="h-5 w-5 text-white" strokeWidth={3} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[15.5px] font-bold leading-snug tracking-[-0.2px]">
                      {tDynamic(row.label)}
                    </div>
                    <div className="text-[13px] font-bold text-[#15776d]">{t("ok")}</div>
                  </div>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void undoAnswer(row.id)}
                    className="px-1.5 py-3 text-[13.5px] font-bold text-[#8494a2]"
                  >
                    {t("undo")}
                  </button>
                </div>
              );
            }
            if (ans === "problem") {
              return (
                <div
                  key={row.id}
                  className="flex items-center gap-3 rounded-2xl border-[1.5px] border-[#e6bdb7] bg-white px-3.5 py-3.5"
                >
                  <div className="relative h-[54px] w-[54px] shrink-0 overflow-hidden rounded-xl border-[1.5px] border-[#dc2626] bg-[#eff4f7]">
                    {photo ? <MediaThumb url={photo} /> : null}
                    <div className="absolute bottom-1 right-1 flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[#dc2626]">
                      <AlertTriangle className="h-2.5 w-2.5 text-white" strokeWidth={3} />
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[15.5px] font-bold leading-snug tracking-[-0.2px]">
                      {tDynamic(row.label)}
                    </div>
                    <div className="text-[13px] font-bold text-[#b4453a]">{t("problem")}</div>
                  </div>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => openProblemCamera(row.id)}
                    className="px-1.5 py-3 text-[13.5px] font-bold text-[#8494a2]"
                  >
                    {t("retake")}
                  </button>
                </div>
              );
            }
            return (
              <div
                key={row.id}
                className="flex flex-col gap-3 rounded-2xl border border-[#e6edf1] bg-white p-3.5"
              >
                <div className="text-[16.5px] font-bold leading-snug tracking-[-0.2px]">
                  {tDynamic(row.label)}
                </div>
                <div className="flex gap-2.5">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void markOk(row.id)}
                    className="flex h-[52px] flex-1 items-center justify-center gap-2 rounded-[13px] bg-[#e2eeed] text-[16px] font-bold text-[#15776d]"
                  >
                    <Check className="h-[18px] w-[18px]" strokeWidth={3} />
                    {t("ok")}
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => openProblemCamera(row.id)}
                    className="flex h-[52px] flex-1 items-center justify-center gap-2 rounded-[13px] border-[1.5px] border-[#f0cbc6] bg-[#fdf4f3] text-[16px] font-bold text-[#b4453a]"
                  >
                    <Camera className="h-4 w-4" strokeWidth={2} />
                    {t("problem")}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className={cn(CLEANER_SIMPLE_CTA_FOOTER, "border-t border-[#e6edf1]/60 bg-white")}>
        <button
          type="button"
          disabled={busy || !allDone}
          onClick={() => void finish()}
          className={cn(
            "flex h-14 w-full items-center justify-center rounded-[15px] text-[17.5px] font-bold",
            allDone
              ? "bg-[#24a89a] text-white hover:bg-[#1f958a] active:bg-[#1b8378]"
              : "bg-[#cfd9de] text-white",
          )}
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : t("next")}
        </button>
      </div>
    </div>
  );
}
